import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/ui/Toast';
import { Button, Card, Badge } from '../../components/ui';
import { ConfirmModal } from '../../components/ui/Modal';

const STATO_BADGE = {
  condiviso: { label: 'Condiviso', class: 'badge-success' },
  condiviso_parziale: { label: 'Parziale', class: 'badge-warning' },
  non_condiviso: { label: 'Non condiviso', class: 'badge-danger' },
  non_trovato: { label: 'Calendario mancante', class: 'badge-gray' },
  errore_permessi: { label: 'Errore', class: 'badge-danger' }
};

function CalendariAdminPage() {
  const { apiRequest } = useAuth();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [shareEmail, setShareEmail] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await apiRequest('/admin/calendari/stato-condivisioni');
      setData(result);
    } catch (error) {
      console.error('Error fetching calendar status:', error);
      showToast('Errore nel caricamento dei dati', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [apiRequest]);

  const handleCreaMancanti = async () => {
    setActionLoading(true);
    try {
      const result = await apiRequest('/admin/calendari/crea-mancanti', { method: 'POST' });
      showToast(result.message, 'success');
      fetchData();
    } catch (error) {
      console.error('Error creating calendars:', error);
      showToast(error.message || 'Errore nella creazione calendari', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCondividiTutti = async () => {
    setActionLoading(true);
    try {
      const result = await apiRequest('/admin/calendari/condividi-tutti', { method: 'POST' });
      showToast(result.message, 'success');
      fetchData();
    } catch (error) {
      console.error('Error sharing calendars:', error);
      showToast(error.message || 'Errore nella condivisione', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const openShareModal = (item) => {
    setSelectedCalendar(item);
    setShareEmail(item.emailDirezione || '');
    setShareModalOpen(true);
  };

  const handleShare = async () => {
    if (!selectedCalendar?.calendario?.id || !shareEmail) return;

    setActionLoading(true);
    try {
      await apiRequest(`/admin/calendari/${selectedCalendar.calendario.id}/condividi`, {
        method: 'POST',
        body: JSON.stringify({ email: shareEmail, role: 'read' })
      });
      showToast('Calendario condiviso con successo', 'success');
      setShareModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error sharing calendar:', error);
      showToast(error.message || 'Errore nella condivisione', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Calendari RSA</h1>
          <p className="text-gray-600">Verifica e gestisci le condivisioni dei calendari Outlook</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={fetchData}
            disabled={actionLoading}
          >
            Aggiorna
          </Button>
        </div>
      </div>

      {/* Stats */}
      {data?.stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <div className="card text-center">
            <div className="text-2xl font-bold text-gray-900">{data.stats.totaleRsa}</div>
            <div className="text-sm text-gray-500">RSA Totali</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-primary-600">{data.stats.calendariTrovati}</div>
            <div className="text-sm text-gray-500">Calendari</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-green-600">{data.stats.condivisi}</div>
            <div className="text-sm text-gray-500">Condivisi</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-amber-600">{data.stats.condivisiParziale}</div>
            <div className="text-sm text-gray-500">Parziali</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-red-600">{data.stats.nonCondivisi}</div>
            <div className="text-sm text-gray-500">Non condivisi</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-gray-500">{data.stats.nonTrovati}</div>
            <div className="text-sm text-gray-500">Mancanti</div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="card mb-6">
        <h3 className="font-semibold text-gray-900 mb-4">Azioni Massive</h3>
        <div className="flex flex-wrap gap-4">
          {data?.stats?.nonTrovati > 0 && (
            <Button
              onClick={handleCreaMancanti}
              disabled={actionLoading}
              variant="secondary"
            >
              {actionLoading ? 'Creazione...' : `Crea ${data.stats.nonTrovati} Calendari Mancanti`}
            </Button>
          )}
          {data?.stats?.nonCondivisi > 0 && (
            <Button
              onClick={handleCondividiTutti}
              disabled={actionLoading}
            >
              {actionLoading ? 'Condivisione...' : `Condividi ${data.stats.nonCondivisi} Calendari`}
            </Button>
          )}
          {data?.stats?.nonTrovati === 0 && data?.stats?.nonCondivisi === 0 && (
            <p className="text-green-600 flex items-center gap-2">
              <span className="text-xl">&#10004;</span>
              Tutti i calendari sono configurati correttamente
            </p>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <h3 className="font-semibold text-gray-900 mb-4">Dettaglio Calendari</h3>

        {data?.value?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>RSA</th>
                  <th>Calendario</th>
                  <th>Email Direzione</th>
                  <th>Condiviso con</th>
                  <th>Stato</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.value.map((item) => (
                  <tr key={item.rsaId}>
                    <td className="font-medium">{item.rsaNome}</td>
                    <td>
                      {item.calendario ? (
                        <span className="text-sm text-gray-600">{item.calendario.name}</span>
                      ) : (
                        <span className="text-sm text-gray-400 italic">Non trovato</span>
                      )}
                    </td>
                    <td>
                      {item.emailDirezione ? (
                        <span className="text-sm">{item.emailDirezione}</span>
                      ) : (
                        <span className="text-sm text-gray-400 italic">Non configurata</span>
                      )}
                    </td>
                    <td>
                      {item.condivisioni?.length > 0 ? (
                        <div className="space-y-1">
                          {item.condivisioni.map((c, i) => (
                            <div key={i} className="text-sm">
                              <span className="text-gray-700">{c.email}</span>
                              <span className="text-xs text-gray-400 ml-1">({c.role})</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 italic">Nessuno</span>
                      )}
                    </td>
                    <td>
                      <span className={STATO_BADGE[item.stato]?.class || 'badge-gray'}>
                        {STATO_BADGE[item.stato]?.label || item.stato}
                      </span>
                    </td>
                    <td>
                      {item.calendario && item.stato !== 'condiviso' && (
                        <button
                          onClick={() => openShareModal(item)}
                          className="text-primary-600 hover:underline text-sm"
                        >
                          Condividi
                        </button>
                      )}
                      {!item.calendario && (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">Nessuna RSA trovata</p>
        )}
      </div>

      {/* Info Box */}
      <div className="card mt-6 bg-blue-50 border-blue-200">
        <h4 className="font-medium text-blue-900 mb-2">Come funziona</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>&#8226; Ogni RSA ha un calendario dedicato in Outlook (es. "Scadenze - RSA Vigonovo")</li>
          <li>&#8226; I calendari sono nell'account <strong>automation@peralbahc.com</strong></li>
          <li>&#8226; La condivisione funziona solo con email interne (@peralbahc.com)</li>
          <li>&#8226; Gli utenti RSA vedranno il calendario condiviso nel loro Outlook</li>
          <li>&#8226; In fase di onboarding, il calendario viene creato automaticamente</li>
        </ul>
      </div>

      {/* Share Modal */}
      <ConfirmModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        onConfirm={handleShare}
        title={`Condividi calendario ${selectedCalendar?.rsaNome}`}
        confirmLabel="Condividi"
        loading={actionLoading}
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Inserisci l'email con cui condividere il calendario:
          </p>
          <input
            type="email"
            value={shareEmail}
            onChange={(e) => setShareEmail(e.target.value)}
            className="input w-full"
            placeholder="email@peralbahc.com"
          />
          <p className="text-sm text-gray-500">
            L'utente ricevera' accesso in lettura al calendario.
          </p>
        </div>
      </ConfirmModal>
    </div>
  );
}

export default CalendariAdminPage;
